import { getInitData } from "../tg.js";

// Базовый fetch-обёртка. На каждый запрос подкладываем X-Telegram-Init-Data
// (см. apps/api/src/auth/plugins.ts). API-плагин валидирует HMAC и резолвит
// TgUser; нам JWT не нужен, потому что initData короткоживущий по своей сути.
//
// Базовый URL: на проде Caddy маршрутит /api/* на api-сервис, на dev'е Vite
// проксирует тот же путь (см. vite.config.ts).

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  // Доп. query-параметры. null/undefined значения пропускаются.
  query?: Record<string, string | number | boolean | undefined | null>;
  // Для public endpoints — Bearer-токен. По умолчанию используем initData.
  bearer?: string;
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const url = buildUrl(path, opts.query);
  const headers: Record<string, string> = {};
  // Content-Type ставим ТОЛЬКО если реально есть body. Fastify по умолчанию
  // отвергает запросы с "Content-Type: application/json" и пустым телом
  // (FST_ERR_CTP_EMPTY_JSON_BODY) — это ломало DELETE-запросы.
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (opts.bearer) {
    headers.Authorization = `Bearer ${opts.bearer}`;
  } else {
    const initData = getInitData();
    if (initData) headers["X-Telegram-Init-Data"] = initData;
  }

  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }
  const res = await fetch(url, init);

  if (res.status === 204) return undefined as T;

  let parsed: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // Ответ не-JSON (например, plain text от nginx 502). Пробрасываем как есть.
    }
  }

  if (!res.ok) {
    const msg =
      typeof parsed === "object" && parsed !== null && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, parsed);
  }

  return parsed as T;
}

// Чистая функция для сборки URL. Тестируем отдельно.
export function buildUrl(
  path: string,
  query?: Record<string, string | number | boolean | undefined | null>,
): string {
  if (!query) return path;
  const params: string[] = [];
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  if (params.length === 0) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}${params.join("&")}`;
}
